import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputOtpModule } from 'primeng/inputotp';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../../../core/services/auth.service';
import { UserService } from '../../../core/services/user.service';
import { ToastService } from '../../../core/services/toast.service';
import { IUser } from '../../../core/models/user.model';

@Component({
    selector: 'app-totp-setup',
    imports: [
        ReactiveFormsModule,
        ButtonModule,
        InputOtpModule,
        CardModule,
        DividerModule,
        TooltipModule
    ],
    templateUrl: './totp-setup.component.html',
    styleUrl: './totp-setup.component.scss'
})
export class TotpSetupComponent implements OnInit {
  currentUser: IUser | null = null;
  totpEnabled: boolean = false;
  showSetup: boolean = false;
  qrCodeImage: string = '';
  secret: string = '';
  isLoading: boolean = false;

  verificationForm = new FormGroup({
    code: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(6)] })
  });

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadCurrentUser();
  }

  loadCurrentUser() {
    this.userService.getSelf().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.totpEnabled = user.totpEnabled;
      },
      error: () => {
        this.toastService.showToast('Erreur', 'Impossible de charger les informations utilisateur', 'error');
      }
    });
  }

  initTotpSetup() {
    this.isLoading = true;
    this.authService.setupTotp().subscribe({
      next: (response) => {
        this.secret = response.secret;
        this.qrCodeImage = 'data:image/png;base64,' + response.qrCode;
        this.showSetup = true;
        this.isLoading = false;
        this.toastService.showToast(
          'Configuration TOTP',
          'Scannez le QR code avec Microsoft Authenticator et entrez le code généré pour activer.'
        );
      },
      error: () => {
        this.isLoading = false;
        this.toastService.showToast('Erreur', 'Impossible d\'initialiser TOTP', 'error');
      }
    });
  }

  enableTotp() {
    if (this.verificationForm.invalid) {
      this.toastService.showToast('Erreur', 'Veuillez entrer un code valide', 'error');
      return;
    }

    this.isLoading = true;
    const code = parseInt(this.verificationForm.value.code || '0', 10);

    this.authService.enableTotp(this.secret, code).subscribe({
      next: () => {
        this.isLoading = false;
        this.totpEnabled = true;
        this.showSetup = false;
        this.verificationForm.reset();
        this.toastService.showToast(
          'TOTP Activé',
          'L\'authentification à deux facteurs via Microsoft Authenticator a été activée avec succès.'
        );
        this.loadCurrentUser();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.showToast('Erreur', err.error || 'Code invalide', 'error');
      }
    });
  }

  disableTotp() {
    if (!confirm('Êtes-vous sûr de vouloir désactiver l\'authentification TOTP ?')) {
      return;
    }

    this.isLoading = true;
    this.authService.disableTotp().subscribe({
      next: () => {
        this.isLoading = false;
        this.totpEnabled = false;
        this.toastService.showToast(
          'TOTP Désactivé',
          'L\'authentification TOTP a été désactivée.'
        );
        this.loadCurrentUser();
      },
      error: () => {
        this.isLoading = false;
        this.toastService.showToast('Erreur', 'Impossible de désactiver TOTP', 'error');
      }
    });
  }

  cancelSetup() {
    this.showSetup = false;
    this.qrCodeImage = '';
    this.secret = '';
    this.verificationForm.reset();
  }

  copySecret() {
    navigator.clipboard.writeText(this.secret).then(() => {
      this.toastService.showToast('Copié', 'Le secret a été copié dans le presse-papiers');
    }).catch(() => {
      this.toastService.showToast('Erreur', 'Impossible de copier le secret', 'error');
    });
  }
}
